// The `entitlements` value embedded on a child-journal / gift-certificate resource
// is a capability SUMMARY OBJECT — not a list of entitlement records. Confirmed
// shape from the API:
//
//   {
//     "maximum-book-pages": 0, "book-credits": 0,
//     "watch-me-grow": false, "talking-family-tree": false,
//     "milestones": false, "growth-charts": false, "prepaid-shipping": false,
//     "ebook-status": "not_included",
//     "book-cover": null, "book-size": null, "print-finish": null,
//     "access-pass-months": null, "access-pass-expires-at": "2025-01-02",
//     "extra-page-cents": null, "extra-copy-discount-percent": null
//   }
//
// Individual `journal_entitlements` records (the unassigned Credits rows) are a
// different resource with their own kind/description/allocation-group fields —
// see entitlementToPlain in ./hooks.js. Don't conflate the two.

// Tolerates kebab-case (JSON:API), snake_case and camelCase for the same key,
// since casing isn't guaranteed consistent across these newer v3 endpoints.
const summaryValue = (summary, key) => {
  if (!summary || typeof summary !== 'object') return undefined;
  const kebab = key.replace(/_/g, '-');
  const snake = key.replace(/-/g, '_');
  const camel = kebab.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
  for (const candidate of [key, kebab, snake, camel]) {
    if (summary[candidate] !== undefined) return summary[candidate];
  }
  return undefined;
};

// Boolean capability flags. "Shipping" rather than a carrier name, per the
// approved terminology.
const FEATURE_FLAGS = [
  ['watch-me-grow', 'Watch Me Grow'],
  ['talking-family-tree', 'Talking Family Tree'],
  ['milestones', 'Milestones'],
  ['growth-charts', 'Growth Chart'],
  ['prepaid-shipping', 'Shipping'],
];

const humanize = value =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase());

const formatDate = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

export const enabledFeatures = summary =>
  FEATURE_FLAGS.filter(([key]) => summaryValue(summary, key) === true).map(([, label]) => label);

// The single bullet-separated line under a card title, e.g.
//   8.5" × 11" Hardcover Book • 60 pages • Watch Me Grow • Milestones
export const specLine = summary => {
  const parts = [];

  const size = summaryValue(summary, 'book-size');
  const cover = summaryValue(summary, 'book-cover');
  if (size || cover) {
    parts.push([size, cover ? `${humanize(cover)} Book` : 'Book'].filter(Boolean).join(' '));
  }

  const pages = Number(summaryValue(summary, 'maximum-book-pages')) || 0;
  if (pages > 0) parts.push(`${pages} pages`);

  parts.push(...enabledFeatures(summary));

  return parts.join(' • ');
};

// Longer per-line breakdown used by the "Order details" modal.
export const detailLines = summary => {
  const lines = [];

  const size = summaryValue(summary, 'book-size');
  const cover = summaryValue(summary, 'book-cover');
  if (size || cover) {
    lines.push([size, cover ? `${humanize(cover)}` : null].filter(Boolean).join(' '));
  }

  const pages = Number(summaryValue(summary, 'maximum-book-pages')) || 0;
  if (pages > 0) lines.push(`${pages} pages`);

  lines.push(...enabledFeatures(summary));

  const finish = summaryValue(summary, 'print-finish');
  if (finish) lines.push(`${humanize(finish)} finish`);

  const ebook = summaryValue(summary, 'ebook-status');
  if (ebook && ebook !== 'not_included') lines.push(`eBook: ${humanize(ebook)}`);

  const extraPage = summaryValue(summary, 'extra-page-cents') / 100;
  if (extraPage) lines.push(`Extra pages: $${extraPage.toFixed(2)} each`);

  const extraCopy = summaryValue(summary, 'extra-copy-discount-percent');
  if (extraCopy) lines.push(`${extraCopy}% off extra book copies`);

  const expiresAt = summaryValue(summary, 'access-pass-expires-at');
  if (expiresAt) {
    const formatted = formatDate(expiresAt);
    if (formatted) lines.push(`Subscription Expires: ${formatted}`);
  }

  return lines;
};

// Customer-facing label for a single journal_entitlement record. Prefer the
// API-provided description over the SKU, and keep bundle names in full.
export const entitlementLabel = entitlement =>
  entitlement.bundleName || entitlement.description || entitlement.sku || 'Add-on';
